/**
 * IRD Check Queue — Phase 1.
 *
 * Rule-based review queue. Clients land here when their myIR data is
 * stale, unknown, or a liability is overdue / due soon. Priority tier
 * governs row colour. Clicking View opens the Tax Position workspace.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const PRIO_PILL = {
    urgent: { label: "Urgent", cls: "border-red-200 bg-red-50 text-red-700" },
    high:   { label: "High",   cls: "border-amber-200 bg-amber-50 text-amber-700" },
    normal: { label: "Normal", cls: "border-zinc-200 bg-zinc-50 text-zinc-700" },
    low:    { label: "Low",    cls: "border-zinc-100 bg-zinc-50/50 text-zinc-500" },
};

const TAX_TYPE_LABEL = { paye: "PAYE", gst: "GST", income_tax: "Income Tax", mixed: "Mixed" };

export default function TaxCheckQueue() {
    const [data, setData] = useState({ queue: [], counts: {} });
    const [loading, setLoading] = useState(true);

    async function load() {
        try {
            setLoading(true);
            const { data } = await api.get("/tax-position/check-queue");
            setData(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load queue");
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const counts = data.counts || {};

    return (
        <div className="p-6 md:p-8 space-y-5" data-testid="tax-check-queue-page">
            <header className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-[22px] font-bold text-[#0F1115] tracking-tight inline-flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" /> IRD Check Queue
                    </h1>
                    <p className="text-[13px] text-[#5E636E] mt-0.5">Clients whose myIR data is stale, unknown, overdue, or has an upcoming due date.</p>
                </div>
                <Button variant="outline" className="h-9 gap-1.5 text-[12.5px]" onClick={load} disabled={loading}>
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </Button>
            </header>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                    { k: "urgent", lbl: "Urgent", c: counts.urgent || 0, pal: "bg-red-500" },
                    { k: "high",   lbl: "High",   c: counts.high   || 0, pal: "bg-amber-500" },
                    { k: "normal", lbl: "Normal", c: counts.normal || 0, pal: "bg-zinc-500" },
                    { k: "low",    lbl: "Low",    c: counts.low    || 0, pal: "bg-zinc-300" },
                ].map((c) => (
                    <div key={c.k} className="rounded-xl bg-white border border-[#E4E4E7] p-4">
                        <div className="flex items-center gap-3">
                            <div className={`h-9 w-9 rounded-full ${c.pal} text-white flex items-center justify-center`}>
                                <Clock className="h-4 w-4" />
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-[#0F1115] leading-none tabular-nums">{c.c}</div>
                                <div className="text-[12px] text-[#5E636E] mt-1">{c.lbl}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <section className="rounded-xl border border-[#E4E4E7] bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                        <thead className="bg-[#FAFAFA]">
                            <tr className="text-left text-[11px] uppercase tracking-wider text-[#5E636E]">
                                <th className="px-3 py-3">Priority</th>
                                <th className="px-3 py-3">Client</th>
                                <th className="px-3 py-3">Reason</th>
                                <th className="px-3 py-3">Tax type</th>
                                <th className="px-3 py-3">Last IRD check</th>
                                <th className="px-3 py-3">Next due</th>
                                <th className="px-3 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.queue || []).map((r) => {
                                const pill = PRIO_PILL[r.priority] || PRIO_PILL.normal;
                                return (
                                    <tr key={r.client_id} className="border-t border-[#F0F0F0] hover:bg-[#FAFAFA]"
                                        data-testid={`queue-row-${r.client_id}`}>
                                        <td className="px-3 py-3">
                                            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${pill.cls}`}>
                                                {pill.label}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3 font-medium text-[#0F1115]">{r.client_name}</td>
                                        <td className="px-3 py-3 text-[#5E636E]">{(r.reasons || []).join(" · ") || "—"}</td>
                                        <td className="px-3 py-3 text-[#0F1115]">{TAX_TYPE_LABEL[r.tax_type] || r.tax_type}</td>
                                        <td className="px-3 py-3 text-[#5E636E]">
                                            {r.last_ird_check_at
                                                ? new Date(r.last_ird_check_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })
                                                : <span className="italic">Never</span>}
                                        </td>
                                        <td className="px-3 py-3 text-[#5E636E]">{r.next_due_label || "—"}</td>
                                        <td className="px-3 py-3">
                                            <Link to={`/app/tax-position/clients/${r.client_id}`}
                                                className="rounded-md border border-[#E4E4E7] bg-white px-2.5 py-1 text-[12px] font-medium text-[#0F1115] hover:bg-zinc-50">
                                                View / Update
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                            {!loading && !(data.queue || []).length && (
                                <tr><td colSpan={7} className="py-10 text-center text-[13px] text-[#5E636E]">All clear — no clients currently need attention.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
